import React, { FC, memo, useEffect, useState } from "react";
import { Marker } from "@react-google-maps/api";

interface IVehicleMarkerProps {
  position: { lat: number; lng: number };
  imageUrl: string;
  heading: number;
  size?: number;
  onClick?: () => void;
}

// Top-view vehicle marker: draws a top-view vehicle image rotated to face
// the direction of travel. Falls back to a plain (non-rotated) icon if the
// canvas gets CORS-tainted by toDataURL().
const VehicleMarker: FC<IVehicleMarkerProps> = memo(
  ({ position, imageUrl, heading, size = 44, onClick }) => {
    const [marker, setMarker] = useState<google.maps.Marker | null>(null);

    useEffect(() => {
      let cancelled = false;
      if (marker && imageUrl) {
        const img = new Image();
        img.src = imageUrl;
        img.alt = "";
        img.crossOrigin = "anonymous";

        const setPlainIcon = () => {
          if (cancelled) return;
          marker.setOptions({
            icon: {
              url: imageUrl,
              scaledSize: new google.maps.Size(size, size),
              anchor: new google.maps.Point(size / 2, size / 2),
            },
          });
        };

        img.onload = () => {
          if (cancelled) return;
          try {
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;

            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            ctx.save();
            ctx.translate(size / 2, size / 2);
            ctx.rotate((heading * Math.PI) / 180);
            ctx.drawImage(img, -size / 2, -size / 2, size, size);
            ctx.restore();

            marker.setOptions({
              icon: {
                url: canvas.toDataURL(),
                scaledSize: new google.maps.Size(size, size),
                anchor: new google.maps.Point(size / 2, size / 2),
              },
            });
          } catch (e) {
            // Canvas tainted by CORS (toDataURL threw) - fall back to a
            // plain, non-rotated icon instead of crashing the map.
            setPlainIcon();
          }
        };

        // Asset failed to load (CORS/404/etc) - degrade to a plain, static
        // icon instead of leaving the marker with no icon at all.
        img.onerror = () => {
          setPlainIcon();
        };
      }
      return () => {
        cancelled = true;
      };
    }, [marker, imageUrl, heading, size]);

    return (
      <Marker
        position={position}
        onClick={onClick}
        onLoad={(m) => setMarker(m)}
      />
    );
  }
);

VehicleMarker.displayName = "VehicleMarker";

export default VehicleMarker;
